import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, TextField, Typography, Slider } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Project, User } from 'types';
import apiReq from 'core/api';
import { useApiFormErrorHandler } from 'hooks/useApiFormErrorHandler';
import { USE_NEW_SHELL } from 'core/flags';
import { FormPageV2, Button as ShellButton } from 'components/AppShell';
import UserSelect from './UserSelect';

interface FormErrors {
  name?: string;
  description?: string;
  users?: string;
  yandex_company_id?: string;
  yandex_token?: string;
}

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [apiError, setApiError] = useState<string>('');
  const handleApiFormError = useApiFormErrorHandler();

  const [formData, setFormData] = useState<Project>({
    name: '',
    description: '',
    users: [],
    yandex_company_id: '',
    yandex_token: '',
    max_tokens: 1000,
  });

  const [users, setUsers] = useState<User[]>([]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadProject = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const project = await apiReq<Project>(`project/${id}/view`);
      if (project) {
        setFormData({
          name: project.name,
          description: project.description || '',
          users: project.users || [],
          yandex_company_id: project.yandex_company_id || '',
          yandex_token: (project as Project & { yandex_token?: string }).yandex_token || '',
          max_tokens: project.max_tokens || 1000,
        });
      }
    } catch (error) {
      setApiError('Ошибка загрузки проекта');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiReq<User[]>('user/all');
      setUsers(data || []);
    } catch (error) {
      setApiError('Ошибка загрузки пользователей');
    }
  }, []);

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название обязательно';
    }

    if (!formData.users || !Array.isArray(formData.users) || formData.users.length === 0) {
      newErrors.users = 'Необходимо выбрать хотя бы одного пользователя';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError('');

      // Преобразуем массив пользователей в массив ID
      const usersIdList = formData.users?.map((user) => user.id) || [];
      const projectData = { ...formData, users: usersIdList };

      let response;
      if (isEdit) {
        console.log('projectData', projectData);
        response = await apiReq<Project>(`project/${id}/update`, 'POST', {
          ...projectData,
        });
      } else {
        response = await apiReq<Project>(`projects`, 'POST', projectData);
      }

      if (response) {
        navigate('/projects');
      } else {
        setApiError('Ошибка сохранения проекта');
      }
    } catch (error) {
      handleApiFormError(error, setErrors, setApiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange =
    (field: 'name' | 'description' | 'yandex_company_id' | 'yandex_token') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      // Очищаем ошибку при изменении поля
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleMaxTokensChange = (event: Event, newValue: number | number[]) => {
    const value = typeof newValue === 'number' ? newValue : newValue[0];
    setFormData((prev) => ({ ...prev, max_tokens: value }));
  };

  const handleMaxTokensInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1000 && value <= 100000) {
      setFormData((prev) => ({ ...prev, max_tokens: value }));
    }
  };

  const handleUsersChange = (selectedUsers: User[]) => {
    setFormData((prev) => ({ ...prev, users: selectedUsers }));

    if (errors.users) {
      setErrors((prev) => ({ ...prev, users: undefined }));
    }
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (isEdit) {
      loadProject();
    }
  }, [isEdit, loadProject]);

  const fields = (
    <Box component="form" id="project-form" onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Название проекта"
        value={formData.name}
        onChange={handleChange('name')}
        error={Boolean(errors.name)}
        helperText={errors.name}
        margin="normal"
        required
      />
      <TextField
        fullWidth
        label="Описание"
        value={formData.description}
        onChange={handleChange('description')}
        margin="normal"
        multiline
        rows={4}
      />
      <UserSelect
        value={formData.users || null}
        onChange={handleUsersChange}
        options={users}
        error={Boolean(errors.users)}
        helperText={errors.users}
      />
      <TextField
        fullWidth
        label="IAM-токен"
        value={(formData as Project & { yandex_token?: string }).yandex_token || ''}
        onChange={handleChange('yandex_token')}
        margin="normal"
      />
      <TextField
        fullWidth
        label="ID Яндекс Компании"
        value={formData.yandex_company_id}
        onChange={handleChange('yandex_company_id')}
        margin="normal"
        type="number"
      />
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Максимальное количество символов
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Slider
            value={formData.max_tokens || 1000}
            onChange={handleMaxTokensChange}
            aria-labelledby="max-tokens-slider"
            valueLabelDisplay="auto"
            step={1000}
            min={1000}
            max={250000}
            sx={{ flex: 1 }}
          />
          <TextField
            type="number"
            value={formData.max_tokens || 1000}
            onChange={handleMaxTokensInputChange}
            inputProps={{ min: 1000, max: 250000, step: 1000 }}
            sx={{ width: 120 }}
            size="small"
          />
        </Box>
      </Box>
      {!USE_NEW_SHELL && (
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ minWidth: 120 }}>
            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/projects')} disabled={isSubmitting}>
            Отмена
          </Button>
        </Box>
      )}
    </Box>
  );

  if (USE_NEW_SHELL) {
    return (
      <FormPageV2
        title={isEdit ? 'Редактировать проект' : 'Создать проект'}
        backTo="/projects"
        isLoading={isLoading}
        errorBanner={apiError ? <Alert severity="error">{apiError}</Alert> : undefined}
        footer={
          <>
            <ShellButton
              variant="ghost"
              onClick={() => navigate('/projects')}
              disabled={isSubmitting}
            >
              Отмена
            </ShellButton>
            <ShellButton
              variant="primary"
              type="submit"
              form="project-form"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Сохранение…' : 'Сохранить'}
            </ShellButton>
          </>
        }
      >
        {fields}
      </FormPageV2>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')} sx={{ mr: 2 }}>
          Назад
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Редактировать проект' : 'Создать проект'}
        </Typography>
      </Box>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}
      {fields}
    </Box>
  );
};

export default ProjectForm;
